import {CountType, InsightsCountType, RetentionCountType} from 'common/query-builder/query-entry-section/types';
import {FunnelsCountType, TableToggleableMetrics} from 'common/report/funnels/models/constants';
import {AggregationGroupLabel} from 'common/query-builder/types/shared-types';
import {
  ALL_MATH_TYPES,
  COUNT_USERS_ONCE_TYPE,
  FUNNELS_MATH_TYPES,
  MATH_TYPE,
  NUMERIC_MATH_TYPES,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  XAU_MATH_TYPES,
} from 'common/report/constants';
import {TrendChartType} from 'common/report/funnels/models/types';
import {FrequencyViewType} from 'common/report/funnels/models/frequency-chart/types';
import {Segmentation} from 'common/types/reports/query';

import {MetricType} from '../constants';
import {isListOfObjectsSegment} from '../segment-menu/types';
import {PropertySectionItem} from '../properties-menu/item-menu-types';
import {propertyItemToSegmentation} from '../properties-menu/util';
import {FUNNELS_TIME_TO_CONVERSION} from '../properties-menu/constants';

const HELP_DOCS_LINK = `\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/insights#insights-data-functions-operators-and-calculations)`;

/** List of selections derived from selected count type */
export enum SubSelectionTypes {
  Property = `property`,
  NestedProperty = `nestedProperty`,
  AggregationPerUser = `aggregationPerUser`,
  Aggregation = `aggregation`,
  CountUsersOnce = `countUsersOnceType`,
}

export type GroupLabelParams = {[key in AggregationGroupLabel]: string};

export type SelectorLabel = string | ((params: GroupLabelParams) => string);

export type RequiredSelectionsParams = {
  [SubSelectionTypes.Property]: PropertySectionItem;
  [SubSelectionTypes.NestedProperty]: Segmentation;
  [SubSelectionTypes.Aggregation]: MATH_TYPE;
  [SubSelectionTypes.AggregationPerUser]: MATH_TYPE;
  [SubSelectionTypes.CountUsersOnce]: COUNT_USERS_ONCE_TYPE;
};

/**
 * Definition of count type in count menu
 * i.e. Uniques, Total Events, Property per Unique, etc.
 */
export type CountTypeSelector = {
  countType: CountType;

  label: SelectorLabel;
  shortLabel?: SelectorLabel;
  description?: SelectorLabel;

  /** Defines across-users aggregations */
  operator?: MATH_TYPE;
  /** Defines per-user aggregations */
  perUserOperator?: MATH_TYPE;
  /** Defines how to count unique users across segments */
  countUsersOnceType?: COUNT_USERS_ONCE_TYPE;

  /** 1st level aggregation definitions for the given count type */
  aggregations?: AggregationSelector[];
  /** Specifies if the aggregation acts on a property */
  forProperty?: boolean;
  property?: PropertySectionItem;

  expanderIcon?: string;
  /** List of selections required to be able to commit */
  requiredSubSelections?: (({property}: {property: PropertySectionItem}) => SubSelectionTypes[]) | SubSelectionTypes[];
  /** Specifies if the given count type can be committed */
  hasRequiredSelections: (param: RequiredSelectionsParams) => boolean;
  /** Specifies if the sub selections can be expanded */
  canExpandSubSelections: ({
    countType,
    aggregation,
    countUsersOnceType,
  }: {
    countType: CountType;
    aggregation: MATH_TYPE;
    countUsersOnceType?: COUNT_USERS_ONCE_TYPE;
  }) => boolean;
};

/**
 * Definition of aggregation that acts on count type
 * i.e. Sum, Average, Median, etc.
 */
export type AggregationSelector = {
  label: SelectorLabel;
  shortLabel?: SelectorLabel;
  description?: SelectorLabel;

  /** Defines across-users aggregations */
  operator?: MATH_TYPE;
  /** Defines per-user aggregations */
  perUserOperator?: MATH_TYPE;
  /** Defines how to count unique users across segments */
  countUsersOnceType?: COUNT_USERS_ONCE_TYPE;

  /**
   * 2nd level aggregation definitions for the 1st pass aggregation
   * Defines a list of aggregations associated with the given aggregation
   * e.g. Median (2nd level aggregation) Sum (1st pass aggregation) of Price per Unique
   */
  subAggregations?: AggregationSelector[];
  /**
   * Defines aggregations to be displayed in a nested menu
   * e.g. P25, P75, P90 for Percentile
   */
  subSections?: AggregationSelector[];
};

/**
 * Template variables for output copies for the selected count type
 * @example: aggregationOperator: total|average|median
 * @example: aggregationOperatorPerUser: total|average|median
 * @example: propertyName: Price|Revenue
 */
export enum CountSelections {
  AggregationOperator = `aggregationOperator`,
  AggregationOperatorPerUser = `aggregationOperatorPerUser`,
  PropertyName = `propertyName`,
  PropertyValue = `propertyValue`,
  CountUsersOnceType = `countUsersOnceType`,
}

export type CountOutputLabelParams = {[key in CountSelections | AggregationGroupLabel]: string};

export type CountOutputLabel = string | ((params: CountOutputLabelParams) => string);

export const OperatorToString = {
  [ALL_MATH_TYPES.TOTAL]: `Sum`,
  [ALL_MATH_TYPES.AVERAGE]: `Average`,
  [ALL_MATH_TYPES.MEDIAN]: `Median`,
  [ALL_MATH_TYPES.MIN]: `Minimum`,
  [ALL_MATH_TYPES.MAX]: `Maximum`,
  [ALL_MATH_TYPES.P25]: `P25`,
  [ALL_MATH_TYPES.P75]: `P75`,
  [ALL_MATH_TYPES.P90]: `P90`,
  [ALL_MATH_TYPES.P99]: `P99`,
  [ALL_MATH_TYPES.UNIQUE_VALUES]: `Distinct Count`,
  [ALL_MATH_TYPES.HISTOGRAM]: `Distribution`,
  [ALL_MATH_TYPES.UNIQUE]: `Interval (Default)`,
  [ALL_MATH_TYPES.DAU]: `Daily Active Users`,
  [ALL_MATH_TYPES.WAU]: `Weekly Active Users`,
  [ALL_MATH_TYPES.MAU]: `Monthly Active Users`,
};

export const COUNT_USERS_ONCE_OPTIONS = {
  [COUNT_USERS_ONCE_TYPE.MULTIPLE]: {
    label: () => `Per Segment (Default)`,
    selectLabel: () => `Count Per Segment`,
    description: () => `Unique users will be counted in each segment they qualify for`,
    enabledStates: [`enabled`, `disabled`],
  },
  [COUNT_USERS_ONCE_TYPE.LAST]: {
    label: ({timePeriod}) => `Last Time ${timePeriod}`,
    selectLabel: ({timePeriod}) => `Count Once - Last Time ${timePeriod}`,
    description: ({timePeriod}) =>
      `Count users once, from the last time they did this event ${timePeriod.toLowerCase()}`,
    enabledStates: [`enabled`, `required`],
  },
  [COUNT_USERS_ONCE_TYPE.FIRST]: {
    label: ({timePeriod}) => `First Time ${timePeriod}`,
    selectLabel: ({timePeriod}) => `Count Once - First Time ${timePeriod}`,
    description: ({timePeriod}) =>
      `Count users once, from the first time they did this event ${timePeriod.toLowerCase()}`,
    enabledStates: [`enabled`, `required`],
  },
};

export const InsightsProfileCountOutputLabelMappings = new Map<InsightsCountType, CountOutputLabel>([
  [InsightsCountType.Total, `Total Profiles`],
  [
    InsightsCountType.PropertyValues,
    ({aggregationOperator, propertyName}: CountOutputLabelParams) => {
      return `${OperatorToString[aggregationOperator]} of ${propertyName}`;
    },
  ],
]);

export const InsightsCountOutputLabelMappings = new Map<InsightsCountType, CountOutputLabel>([
  [
    InsightsCountType.UniqueUsers,
    ({aggregationOperator, countUsersOnceType, pluralGroupTitle, timePeriod}) => {
      const operatorLabel =
        Object.values(XAU_MATH_TYPES).some((type) => type === aggregationOperator) && aggregationOperator.toUpperCase();
      const countUsersLabel = COUNT_USERS_ONCE_OPTIONS[countUsersOnceType]?.label({timePeriod});
      return [`Unique ${pluralGroupTitle}`, operatorLabel, countUsersLabel].filter(Boolean).join(` - `);
    },
  ],
  [InsightsCountType.Total, `Total Events`],
  [InsightsCountType.TotalSessions, InsightsCountType.TotalSessions],
  [
    InsightsCountType.FrequencyPerUser,
    ({aggregationOperator, groupTitle}: CountOutputLabelParams) => {
      return aggregationOperator === ALL_MATH_TYPES.HISTOGRAM
        ? `Frequency Distribution per ${groupTitle}`
        : `${OperatorToString[aggregationOperator]} Frequency per ${groupTitle}`;
    },
  ],
  [
    InsightsCountType.PropertyValues,
    ({aggregationOperator, propertyName}: CountOutputLabelParams) => {
      return `${OperatorToString[aggregationOperator]} of ${propertyName}`;
    },
  ],
  [
    InsightsCountType.PropertyValuePerUser,
    ({aggregationOperator, aggregationOperatorPerUser, propertyName, groupTitle}: CountOutputLabelParams) => {
      const propertyLabel = `${OperatorToString[aggregationOperatorPerUser]} ${propertyName} per ${groupTitle}`;
      return `${OperatorToString[aggregationOperator]} of (${propertyLabel})`;
    },
  ],
]);

export const RetentionCountOutputLabelMappings = new Map<RetentionCountType, CountOutputLabel>([
  [RetentionCountType.Unique, RetentionCountType.Unique],
  [RetentionCountType.RetentionRate, RetentionCountType.RetentionRate],
]);

export const FunnelsCountOutputLabelMappings = new Map<FunnelsCountType, CountOutputLabel>([
  [FunnelsCountType.Unique, ({pluralGroupTitle}) => `Unique ${pluralGroupTitle}`],
  [FunnelsCountType.Session, `Total Sessions`],
  [FunnelsCountType.ConversionRate, `Conversion Rate`],
  [FunnelsCountType.PropertySum, `Property Sum`],
  [
    FunnelsCountType.General,
    ({aggregationOperator, propertyName, propertyValue}: CountOutputLabelParams) => {
      if (propertyValue === FUNNELS_TIME_TO_CONVERSION.value) {
        if (aggregationOperator in OperatorToString) {
          return `${OperatorToString[aggregationOperator]} of TTC`;
        } else {
          return `TTC`;
        }
      } else if (propertyValue !== null && propertyValue !== undefined) {
        return `${OperatorToString[aggregationOperator]} of ${propertyName}`;
      } else {
        return `Total Conversions`;
      }
    },
  ],
]);

export const FunnelsCountToTrendMapping = new Map<FunnelsCountType, TrendChartType>([
  [FunnelsCountType.Unique, TrendChartType.TopOfFunnel],
  [FunnelsCountType.General, TrendChartType.TopOfFunnel],
  [FunnelsCountType.Session, TrendChartType.TopOfFunnel],
  [FunnelsCountType.PropertySum, TrendChartType.TopOfFunnel],
  [FunnelsCountType.ConversionRate, TrendChartType.Conversion],
]);

export const FunnelsCountToFrequencyMapping = new Map<FunnelsCountType, FrequencyViewType>([
  [FunnelsCountType.Unique, FrequencyViewType.Conversion],
  [FunnelsCountType.General, FrequencyViewType.Conversion],
  [FunnelsCountType.Session, FrequencyViewType.Conversion],
  [FunnelsCountType.PropertySum, FrequencyViewType.Conversion],
  [FunnelsCountType.ConversionRate, FrequencyViewType.ConversionRate],
]);

export const FunnelsCountToMetricColumnMapping = new Map<FunnelsCountType, TableToggleableMetrics>([
  [FunnelsCountType.Unique, TableToggleableMetrics.Count],
  [FunnelsCountType.General, TableToggleableMetrics.Count],
  [FunnelsCountType.Session, TableToggleableMetrics.Count],
  [FunnelsCountType.PropertySum, TableToggleableMetrics.Count],
  [FunnelsCountType.ConversionRate, TableToggleableMetrics.ConvPrevStep],
]);

export const CountOutputLabelMappings = new Map<MetricType, Map<CountType, CountOutputLabel>>([
  [MetricType.Event, InsightsCountOutputLabelMappings],
  [MetricType.People, InsightsProfileCountOutputLabelMappings],
  [MetricType.Funnel, FunnelsCountOutputLabelMappings],
  [MetricType.Retention, RetentionCountOutputLabelMappings],
]);

export const InsightsCountSubMenuHeader = {
  [InsightsCountType.UniqueUsers]: {
    [SubSelectionTypes.Aggregation]: {
      label: `Aggregate by`,
      description: ({pluralGroup}) =>
        `Select how the number of ${pluralGroup} who perform an event are calculated. ${HELP_DOCS_LINK}`,
      icon: `info-xs`,
    },
  },
  [InsightsCountType.FrequencyPerUser]: {
    [SubSelectionTypes.Aggregation]: {
      label: `Aggregate by`,
      description: ({group}) =>
        `Select how the number of events per ${group} is calculated across all instances of that event. ${HELP_DOCS_LINK}`,
      icon: `info-xs`,
    },
  },
  [InsightsCountType.PropertyValues]: {
    [SubSelectionTypes.Aggregation]: {
      label: `Aggregate by`,
      description: `Select how property values from each instance of an event are calculated. ${HELP_DOCS_LINK}`,
      icon: `info-xs`,
    },
  },
  [InsightsCountType.PropertyValuePerUser]: {
    [SubSelectionTypes.AggregationPerUser]: {
      label: ({groupTitle}) => `Aggregate per ${groupTitle} by`,
      description: `Select how property values from each instance of an event are calculated. ${HELP_DOCS_LINK}`,
      icon: `info-xs`,
    },
    [SubSelectionTypes.Aggregation]: {
      label: `Aggregate by`,
      description: ({group}) =>
        `Select how the number of events per ${group} is calculated across all instances of that event. ${HELP_DOCS_LINK}`,
      icon: `info-xs`,
    },
  },
};

const UniqueAggregations: AggregationSelector[] = [
  {
    label: `Interval (Default)`,
    operator: PRIMITIVE_MATH_TYPES.UNIQUE,
    description: ({pluralGroup}) =>
      `The number of ${pluralGroup} who performed an event in the defined chart interval range${HELP_DOCS_LINK}`,
  },
  {
    label: ({pluralGroupTitle}) => `Daily Active ${pluralGroupTitle}`,
    operator: XAU_MATH_TYPES.DAU,
    description: ({pluralGroup}) =>
      `The number of ${pluralGroup} who performed an event within the last 24 hours${HELP_DOCS_LINK}`,
  },
  {
    label: ({pluralGroupTitle}) => `Weekly Active ${pluralGroupTitle}`,
    operator: XAU_MATH_TYPES.WAU,
    description: ({pluralGroup}) =>
      `The number of ${pluralGroup} who performed an event within the last 7 days${HELP_DOCS_LINK}`,
  },
  {
    label: ({pluralGroupTitle}) => `Monthly Active ${pluralGroupTitle}`,
    operator: XAU_MATH_TYPES.MAU,
    description: ({pluralGroup}) =>
      `The number of ${pluralGroup} who performed an event within the last 30 days${HELP_DOCS_LINK}`,
  },
];

const FrequencyPerUniqueAggregations: AggregationSelector[] = [
  {
    label: `Distribution`,
    operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}) =>
      `A distribution of the number of times an event is performed per ${group}${HELP_DOCS_LINK}`,
  },
  {
    label: `Average`,
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}) => `Average across all users of the event count per ${group}${HELP_DOCS_LINK}`,
  },
  {
    label: `Median`,
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}) => `Median of the event count per ${group}${HELP_DOCS_LINK}`,
  },
  {
    label: `Percentile`,
    subSections: [
      {
        label: `25th Percentile`,
        shortLabel: `P25`,
        operator: NUMERIC_MATH_TYPES.P25,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}) => `The 25th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `75th Percentile`,
        shortLabel: `P75`,
        operator: NUMERIC_MATH_TYPES.P75,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}) => `The 75th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: `90th Percentile`,
        shortLabel: `P90`,
        operator: NUMERIC_MATH_TYPES.P90,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}) => `The 90th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
    ] as AggregationSelector[],
  },
  {
    label: `Minimum`,
    operator: PRIMITIVE_MATH_TYPES.MIN,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}) => `Minimum of the number of time a ${group} performed an event${HELP_DOCS_LINK}`,
  },
  {
    label: `Maximum`,
    operator: PRIMITIVE_MATH_TYPES.MAX,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}) => `Maximum of the number of time a ${group} performed an event${HELP_DOCS_LINK}`,
  },
];

const PropertyAggregations: AggregationSelector[] = [
  {
    label: `Sum`,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: `Counts the total of a numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    label: `Average`,
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    description: `Adds the numeric property values across all profiles and divides by the total count${HELP_DOCS_LINK}`,
  },
  {
    label: `Median`,
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    description: `Counts the numeric property value across all profiles in order and finds the middle value${HELP_DOCS_LINK}`,
  },
  {
    label: `Distinct Count`,
    operator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
    description: `The number of distinct property values across all profiles${HELP_DOCS_LINK}`,
  },
  {
    label: `Percentile`,
    subSections: [
      {
        label: `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        shortLabel: `P25`,
        description: `Ranks the 25th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
      {
        label: `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        shortLabel: `P75`,
        description: `Ranks the 75th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
      {
        label: `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        shortLabel: `P90`,
        description: `Ranks the 90th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
      {
        label: `99th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P99,
        shortLabel: `P99`,
        description: `Ranks the 99th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
    ] as AggregationSelector[],
  },
  {
    label: `Minimum`,
    operator: PRIMITIVE_MATH_TYPES.MIN,
    description: `The smallest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
  },
  {
    label: `Maximum`,
    operator: PRIMITIVE_MATH_TYPES.MAX,
    description: `The greatest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
  },
];

const TTCPropertyAggregations: AggregationSelector[] = [
  {
    label: `Average`,
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    description: `Adds the numeric property values across all profiles and divides by the total count${HELP_DOCS_LINK}`,
  },
  {
    label: `Median`,
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    description: `Counts the numeric property value across all profiles in order and finds the middle value${HELP_DOCS_LINK}`,
  },
  {
    label: `Percentile`,
    subSections: [
      {
        label: `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        shortLabel: `P25`,
        description: `Ranks the 25th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
      {
        label: `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        shortLabel: `P75`,
        description: `Ranks the 75th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
      {
        label: `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        shortLabel: `P90`,
        description: `Ranks the 90th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
      {
        label: `99th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P99,
        shortLabel: `P99`,
        description: `Ranks the 99th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
      },
    ] as AggregationSelector[],
  },
  {
    label: `Minimum`,
    operator: PRIMITIVE_MATH_TYPES.MIN,
    description: `The smallest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
  },
  {
    label: `Maximum`,
    operator: PRIMITIVE_MATH_TYPES.MAX,
    description: `The greatest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
  },
];

const PerUserSum = {
  label: `Sum`,
  perUserOperator: PRIMITIVE_MATH_TYPES.TOTAL,
  description: ({group}) => `The total of numeric property values per ${group}${HELP_DOCS_LINK}`,
};
const PerUserAverage = {
  label: `Average`,
  perUserOperator: PRIMITIVE_MATH_TYPES.AVERAGE,
  description: ({group}) => `Average of numeric property values per ${group}${HELP_DOCS_LINK}`,
};
const PerUserDistinctCount = {
  label: `Distinct Count`,
  perUserOperator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
  description: ({group}) => `The number of distinct property values per ${group}${HELP_DOCS_LINK}`,
};
const PerUserMinimum = {
  label: `Minimum`,
  perUserOperator: PRIMITIVE_MATH_TYPES.MIN,
  description: ({group}) => `Minimum of all the numeric property values per ${group}${HELP_DOCS_LINK}`,
};
const PerUserMaximum = {
  label: `Maximum`,
  perUserOperator: PRIMITIVE_MATH_TYPES.MAX,
  description: ({group}) => `Maximum of all the numeric property values per ${group}${HELP_DOCS_LINK}`,
};

export const PropertyPerUniqueAggregations: AggregationSelector[] = [
  {
    label: `Average`,
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    description: ({group}) => `Average of property value counts per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
      (aggregator) => ({
        ...aggregator,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
      }),
    ),
  },
  {
    label: `Sum`,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: ({group}) => `Sum of property value counts per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map((aggregator) => ({
      ...aggregator,
      operator: PRIMITIVE_MATH_TYPES.TOTAL,
    })),
  },
  {
    label: `Distribution`,
    operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
    description: ({pluralGroup}) => `A distribution of ${pluralGroup} by property value counts${HELP_DOCS_LINK}`,
    subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
      (aggregator) => ({
        ...aggregator,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
      }),
    ),
  },
  {
    label: `Median`,
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    description: ({group}) => `Median of property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
      (aggregator) => ({
        ...aggregator,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
      }),
    ),
  },
  {
    label: `Percentile`,
    subSections: [
      {
        label: `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        shortLabel: `P25`,
        description: ({group}) => `The 25th percentile of property value counts per ${group}${HELP_DOCS_LINK}`,
        subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
          (aggregator) => ({
            ...aggregator,
            operator: PRIMITIVE_MATH_TYPES.P25,
          }),
        ),
      },
      {
        label: `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        shortLabel: `P75`,
        description: ({group}) => `The 75th percentile of property value counts per ${group}${HELP_DOCS_LINK}`,
        subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
          (aggregator) => ({
            ...aggregator,
            operator: PRIMITIVE_MATH_TYPES.P75,
          }),
        ),
      },
      {
        label: `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        shortLabel: `P90`,
        description: ({group}) => `The 90th percentile of property value counts per ${group}${HELP_DOCS_LINK}`,
        subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
          (aggregator) => ({
            ...aggregator,
            operator: PRIMITIVE_MATH_TYPES.P90,
          }),
        ),
      },
    ] as AggregationSelector[],
  },
  {
    label: `Minimum`,
    operator: PRIMITIVE_MATH_TYPES.MIN,
    description: ({group}) => `Minimum of property value counts per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMaximum].map((aggregator) => ({
      ...aggregator,
      operator: PRIMITIVE_MATH_TYPES.MIN,
    })),
  },
  {
    label: `Maximum`,
    operator: PRIMITIVE_MATH_TYPES.MAX,
    description: ({group}) => `Maximum of property value counts per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum].map((aggregator) => ({
      ...aggregator,
      operator: PRIMITIVE_MATH_TYPES.MAX,
    })),
  },
];

const needsNestedProperty = ({property}: {property: PropertySectionItem}): boolean =>
  property && isListOfObjectsSegment(propertyItemToSegmentation(property, property?.resourceType));

const hasRequiredPropertyValues = ({
  property,
  nestedProperty,
}: {
  property: PropertySectionItem;
  nestedProperty: Segmentation;
}) => (needsNestedProperty({property}) ? !!nestedProperty && !!property : !!property);

export const PROFILE_COUNT_MENU_ITEMS: CountTypeSelector[] = [
  {
    countType: InsightsCountType.Total,
    label: `Total Profiles`,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: `The total number profiles`,
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: InsightsCountType.PropertyValues,
    label: InsightsCountType.PropertyValues,
    description: `Aggregate property value across all instances of an event${HELP_DOCS_LINK}`,
    aggregations: PropertyAggregations,
    forProperty: true,
    requiredSubSelections: ({property}) =>
      [
        SubSelectionTypes.Aggregation,
        SubSelectionTypes.Property,
        needsNestedProperty({property}) ? SubSelectionTypes.NestedProperty : null,
      ].filter(Boolean),
    hasRequiredSelections: ({property, aggregation, nestedProperty}) =>
      hasRequiredPropertyValues({property, nestedProperty}) && !!aggregation,
    canExpandSubSelections: ({countType}) => !!countType,
  },
];

export const INSIGHTS_COUNT_MENU_ITEMS: CountTypeSelector[] = [
  {
    countType: InsightsCountType.UniqueUsers,
    label: ({pluralGroupTitle}) => `Unique ${pluralGroupTitle}`,
    operator: PRIMITIVE_MATH_TYPES.UNIQUE,
    description: ({pluralGroup}) => `The number of ${pluralGroup} who performed an event${HELP_DOCS_LINK}`,
    expanderIcon: `caret-right`,
    aggregations: UniqueAggregations,
    requiredSubSelections: [SubSelectionTypes.Aggregation, SubSelectionTypes.CountUsersOnce],
    hasRequiredSelections: ({aggregation, countUsersOnceType}) => Boolean(aggregation || countUsersOnceType),
    /** can expand sub selections only is an operator is XAU and count type is selected */
    canExpandSubSelections: ({countType, aggregation, countUsersOnceType}) =>
      Boolean(countUsersOnceType) ||
      (countType &&
        [XAU_MATH_TYPES.DAU, XAU_MATH_TYPES.WAU, XAU_MATH_TYPES.MAU].includes(aggregation as XAU_MATH_TYPES)),
  },
  {
    countType: InsightsCountType.Total,
    label: `Total Events`,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: `The total number of events performed${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: InsightsCountType.TotalSessions,
    label: InsightsCountType.TotalSessions,
    operator: PRIMITIVE_MATH_TYPES.SESSIONS,
    description: `The total number of sessions that contain the selected event${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: InsightsCountType.FrequencyPerUser,
    label: ({groupTitle}) => `Frequency per ${groupTitle}`,
    description: ({group}) => `The number of events performed per ${group}${HELP_DOCS_LINK}`,
    aggregations: FrequencyPerUniqueAggregations,
    requiredSubSelections: [SubSelectionTypes.Aggregation],
    hasRequiredSelections: ({aggregation}) => !!aggregation,
    canExpandSubSelections: ({countType}) => !!countType,
  },
  {
    countType: InsightsCountType.PropertyValues,
    label: InsightsCountType.PropertyValues,
    description: `Aggregate property value across all instances of an event${HELP_DOCS_LINK}`,
    aggregations: PropertyAggregations,
    forProperty: true,
    requiredSubSelections: ({property}) =>
      [
        SubSelectionTypes.Aggregation,
        SubSelectionTypes.Property,
        needsNestedProperty({property}) ? SubSelectionTypes.NestedProperty : null,
      ].filter(Boolean),
    hasRequiredSelections: ({property, aggregation, nestedProperty}) =>
      hasRequiredPropertyValues({property, nestedProperty}) && !!aggregation,
    canExpandSubSelections: ({countType}) => !!countType,
  },
  {
    countType: InsightsCountType.PropertyValuePerUser,
    label: ({groupTitle}) => `Aggregate Property per ${groupTitle}`,
    description: ({group}) =>
      `Aggregate property values across all instances of an event per ${group}${HELP_DOCS_LINK}`,
    aggregations: PropertyPerUniqueAggregations,
    forProperty: true,
    requiredSubSelections: ({property}) =>
      [
        SubSelectionTypes.Aggregation,
        SubSelectionTypes.AggregationPerUser,
        SubSelectionTypes.Property,
        needsNestedProperty({property}) ? SubSelectionTypes.NestedProperty : null,
      ].filter(Boolean),
    hasRequiredSelections: ({nestedProperty, property, aggregationPerUser, aggregation}) =>
      hasRequiredPropertyValues({property, nestedProperty}) && !!property && !!aggregationPerUser && !!aggregation,
    canExpandSubSelections: ({countType}) => !!countType,
  },
];

export const RETENTION_COUNT_MENU_ITEMS: CountTypeSelector[] = [
  {
    countType: RetentionCountType.RetentionRate,
    operator: RETENTION_MATH_TYPES.RETENTION_RATE,
    label: `Retention Rate`,
    description: `Count the percentage rate for users who were retained within window Learn more${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: RetentionCountType.Unique,
    operator: RETENTION_MATH_TYPES.UNIQUE,
    label: ({pluralGroupTitle}) => `Unique ${pluralGroupTitle}`,
    description: ({pluralGroupTitle}) => `Count ${pluralGroupTitle} who were retained within window${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
];

export const FUNNELS_COUNT_MENU_ITEMS: CountTypeSelector[] = [
  {
    countType: FunnelsCountType.ConversionRate,
    operator: FUNNELS_MATH_TYPES.CONVERSION_RATE,
    label: `Conversion Rate`,
    description: `Count multiple entries per user. User may re-enter after converting or falling off${HELP_DOCS_LINK}`, // TODO atm this help doc is insights only
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: FunnelsCountType.Unique,
    operator: FUNNELS_MATH_TYPES.UNIQUE,
    label: ({pluralGroupTitle}) => `Unique ${pluralGroupTitle}`,
    description: `Count a single entry per user${HELP_DOCS_LINK}`, // TODO atm this help doc is insights only
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: FunnelsCountType.General,
    operator: FUNNELS_MATH_TYPES.GENERAL,
    label: `Total Conversions`,
    description: `Count multiple entries per user. User may re-enter after converting or dropping off${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: FunnelsCountType.Session,
    operator: FUNNELS_MATH_TYPES.SESSION,
    label: `Total Sessions`,
    description: `Count the number of sessions that convert${HELP_DOCS_LINK}`,
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: FunnelsCountType.General,
    label: `Time to Convert`,
    description: `Count multiple entries per user. User may re-enter after converting or falling off${HELP_DOCS_LINK}`, // TODO atm this help doc is insights only
    aggregations: TTCPropertyAggregations,
    forProperty: true,
    property: FUNNELS_TIME_TO_CONVERSION,
    requiredSubSelections: () => [SubSelectionTypes.Aggregation],
    hasRequiredSelections: ({aggregation}) => !!aggregation,
    canExpandSubSelections: ({countType}) => !!countType,
  },
  {
    countType: FunnelsCountType.PropertySum,
    operator: FUNNELS_MATH_TYPES.PROPERTY_SUM,
    label: `Property Sum`,
    description: `Count the numeric property value across all instances on all conversion / funnel events.`,
    forProperty: true,
    requiredSubSelections: ({property}) =>
      [SubSelectionTypes.Property, needsNestedProperty({property}) ? SubSelectionTypes.NestedProperty : null].filter(
        Boolean,
      ),
    hasRequiredSelections: ({property, nestedProperty}) => hasRequiredPropertyValues({property, nestedProperty}),
    canExpandSubSelections: ({countType}) => !!countType,
  },
];
